import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { useState } from 'react';
import debounce from 'react-utils/debounce';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { AddIcon } from 'foundations-assets';
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import { BandType, updateSnowflakeReportEncodingWithConditionalFormatting } from '../utils';
import ConditionalFormattingMinAndMaxInputControl from '../ConditionalFormattingMinAndMaxInputControl';
import { Small } from 'foundations-components/transitional/components/Small';
import { MILLISECONDS } from 'reporting-data/constants/timeUnits';
import { DurationSelect } from '../../shared-components/DurationSelect';
import { EVENTS } from '../../../lib/events';
import { useSnowflakeReportUpdater } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { fromJS } from 'immutable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SnowflakeConditionalFormattingMinAndMax = ({
  report,
  reportDeps,
  onChange,
  selectedColumnName,
  tracker
}) => {
  var _snowflakeReport$visu, _snowflakeProperty$me, _selectedColumnEncodi;
  const [showMidpoint, setShowMidpoint] = useState(false);
  const snowflakeReport = createRelationalReportFromBaseReport(report);
  const {
    columns
  } = snowflakeReport;
  const snowflakeUpdater = useSnowflakeReportUpdater(snowflakeReport);
  const encodings = (_snowflakeReport$visu = snowflakeReport.visual) === null || _snowflakeReport$visu === void 0 ? void 0 : _snowflakeReport$visu.encodings;
  const {
    tableDescriptionMeta
  } = reportDeps;
  const {
    properties
  } = tableDescriptionMeta;
  const selectedColumn = selectedColumnName ? columns.get(selectedColumnName) : undefined;
  if (!selectedColumn) {
    return null;
  }
  const snowflakeProperty = properties && properties.getIn([selectedColumn.field.table, selectedColumn.field.name]);
  const showDurationControl = (snowflakeProperty === null || snowflakeProperty === void 0 || (_snowflakeProperty$me = snowflakeProperty.metaDefinition) === null || _snowflakeProperty$me === void 0 ? void 0 : _snowflakeProperty$me.numberDisplayHint) === 'duration';
  const selectedColumnEncoding = encodings.get('columns').find(encodedColumn => encodedColumn.column === (selectedColumn === null || selectedColumn === void 0 ? void 0 : selectedColumn.get('alias')));
  const existingConditionalFormatting = (selectedColumnEncoding === null || selectedColumnEncoding === void 0 || (_selectedColumnEncodi = selectedColumnEncoding.getIn(['options', 'format', 'conditionalFormatting'])) === null || _selectedColumnEncodi === void 0 ? void 0 : _selectedColumnEncodi.toJS()) || {};
  const {
    min,
    max,
    mid,
    minColor,
    maxColor,
    midColor,
    palette: currentPalette
  } = existingConditionalFormatting;
  const updateFormattingParameters = newFormatting => {
    const toIndex = encodings.get('columns').indexOf(selectedColumnEncoding);
    const updatedConditionalFormatting = Object.assign({}, existingConditionalFormatting, newFormatting);
    const updatedEncoding = selectedColumnEncoding.setIn(['options', 'format', 'conditionalFormatting'], fromJS(updatedConditionalFormatting));
    return updateSnowflakeReportEncodingWithConditionalFormatting({
      toIndex,
      updatedEncoding,
      snowflakeUpdater
    });
  };
  const updateDuration = duration => {
    tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
      actionType: 'updateDuration',
      duration
    });
    const updatedEncoding = selectedColumnEncoding.setIn(['options', 'format', 'duration'], duration);
    const toIndex = encodings.get('columns').indexOf(selectedColumnEncoding);
    return updateSnowflakeReportEncodingWithConditionalFormatting({
      toIndex,
      updatedEncoding,
      snowflakeUpdater
    });
  };
  const handleChange = (newFormatting, actionType) => {
    if (actionType) {
      tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
        actionType
      });
    }
    const nextReport = updateFormattingParameters(newFormatting);
    onChange(nextReport);
  };
  const handleDurationChange = duration => {
    const nextReport = updateDuration(duration);
    onChange(nextReport);
  };
  const selectedDuration = selectedColumnEncoding.getIn(['options', 'format', 'duration']) || MILLISECONDS;
  const debouncedOnChange = debounce(handleChange, 250);
  const durationDisplayText = showDurationControl ? selectedDuration : null;
  return /*#__PURE__*/_jsxs("div", {
    children: [showDurationControl && /*#__PURE__*/_jsx(DurationSelect, {
      selectedDuration: selectedDuration,
      onUpdateDuration: handleDurationChange
    }), /*#__PURE__*/_jsx(ConditionalFormattingMinAndMaxInputControl, {
      onChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        min: value
      }), 'updateMin'),
      onColorChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        minColor: value
      })),
      currentValue: min,
      currentColor: minColor,
      currentPalette: currentPalette,
      bandType: BandType.MIN,
      selectedDuration: durationDisplayText,
      error: min !== undefined && max !== undefined && min > max,
      validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.conditional-formatting.min-max-controls.min.error"
      })
    }), showMidpoint ? /*#__PURE__*/_jsx(ConditionalFormattingMinAndMaxInputControl, {
      onChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        mid: value
      }), 'updateMid'),
      onColorChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        midColor: value
      })),
      onRemoveMidpoint: () => {
        setShowMidpoint(false);
        debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
          mid: undefined
        }), 'removeMid');
      },
      currentValue: mid,
      currentColor: midColor,
      currentPalette: currentPalette,
      bandType: BandType.MID,
      selectedDuration: durationDisplayText,
      error: min !== undefined && max !== undefined && mid !== undefined && (min > mid || mid > max),
      validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.conditional-formatting.min-max-controls.mid.error"
      })
    }) : /*#__PURE__*/_jsx(FormControl, {
      children: /*#__PURE__*/_jsxs(Button, {
        use: "link",
        onClick: () => {
          tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
            actionType: 'addMid'
          });
          setShowMidpoint(true);
        },
        children: [/*#__PURE__*/_jsx(AddIcon, {
          size: "300"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "report-customization-components.customization-components.conditional-formatting.add-mid-button"
        })]
      })
    }), /*#__PURE__*/_jsx(ConditionalFormattingMinAndMaxInputControl, {
      onChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        max: value
      }), 'updateMax'),
      onColorChange: value => debouncedOnChange(Object.assign({}, existingConditionalFormatting, {
        maxColor: value
      })),
      currentValue: max,
      currentColor: maxColor,
      currentPalette: currentPalette,
      bandType: BandType.MAX,
      selectedDuration: durationDisplayText,
      error: min !== undefined && max !== undefined && min > max,
      validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.conditional-formatting.min-max-controls.max.error"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "report-customization-components.customization-components.conditional-formatting.auto-minmax-info"
        })
      })
    })]
  });
};